import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import MessagePopup from "../components/messagePopup";

/**
 * Centralized Toast context.
 *
 * Wrap your app with <ToastProvider> once (e.g. in App.js), then call
 * toast.success / toast.error / toast.warning from any component:
 *
 *   const toast = useToast();
 *   toast.success("Saved!");
 *   toast.error("Something went wrong.");
 *   toast.warning("Check this out.");
 */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("error");
    const [requireConfirmation, setRequireConfirmation] = useState(false);

    // Use a ref so showToast never becomes stale inside callbacks
    const show = useCallback((type, msg, options = {}) => {
        setMessageType(type);
        setMessage(msg);
        setRequireConfirmation(options.requireConfirmation || false);
    }, []);

    const toast = useRef({
        success: (msg, options) => show("success", msg, options),
        error: (msg, options) => show("error", msg, options),
        warning: (msg, options) => show("warning", msg, options),
    }).current;

    const handleClose = useCallback(() => {
        setMessage("");
    }, []);

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <MessagePopup
                message={message}
                messageType={messageType}
                onClose={handleClose}
                requireConfirmation={requireConfirmation}
            />
        </ToastContext.Provider>
    );
}

/**
 * Returns the toast object with { success, error, warning } methods.
 * Must be called inside a component that is a descendant of <ToastProvider>.
 */
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error("useToast must be used within a <ToastProvider>");
    }
    return ctx;
}
